import {
  useAdminCreateGroup,
  useAdminUploadGroupImage,
  useAdminUploadGroupVideo,
} from "@/hooks/api/useAdminGroupsAPI";
import { type CreateGroupFormData } from "@/components/molecules/CreateGroupForm";

interface UseCreateGroupModalProps {
  onClose: () => void;
  onSuccess?: (groupId: string) => void;
}

export const useCreateGroupModal = ({
  onClose,
  onSuccess,
}: UseCreateGroupModalProps) => {
  const createGroupMutation = useAdminCreateGroup();
  const uploadImageMutation = useAdminUploadGroupImage();
  const uploadVideoMutation = useAdminUploadGroupVideo();

  const handleCreateGroup = async (formData: CreateGroupFormData) => {
    try {
      // Extract files before sending to API
      const { image, video, ...apiData } = formData;

      // Create group with API data (no files)
      const response = await createGroupMutation.mutateAsync(apiData);
      const groupId = response.data?._id;

      if (!groupId) {
        throw new Error("Group created but no ID returned");
      }

      // Close modal immediately
      onClose();

      // Upload files in background if they exist
      if (image) {
        uploadImageMutation
          .mutateAsync({ groupId, file: image })
          .then(() => console.log("✅ Image uploaded"))
          .catch((err) => console.error("❌ Image upload failed:", err));
      }

      if (video) {
        uploadVideoMutation
          .mutateAsync({ groupId, file: video })
          .then(() => console.log("✅ Video uploaded"))
          .catch((err) => console.error("❌ Video upload failed:", err));
      }

      // Call success callback
      if (onSuccess) {
        onSuccess(groupId);
      }
    } catch (error: any) {
      console.error("Failed to create group:", error);
      alert(error.message || "Failed to create group");
    }
  };

  return {
    handleCreateGroup,
    isPending: createGroupMutation.isPending,
  };
};
